import { Injectable } from '@angular/core'
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http'
import { Observable, throwError, empty } from 'rxjs'
import { HttpUrlEncodingCodec } from './../services/auth/encoder'
import { Fund } from '@models/fund.model'
import { PossibleValue } from '@models/possible-value.model'
import { environment } from '@env/environment'
import { map } from "rxjs/operators";
import { SettlementInstruction } from "@models/settlement-instruction.model";
import { CounterParty } from "@models/counter-party.model";
import { ProfileManagerService } from '@app/custom-shared/services/profile-manager.service';
import { LocalService } from '@app/shared/services/local.service'
const BASE_PATH = environment.basePath;
const FUNDO_COTA_PATH = environment.basePath + "/bff/externalFund/all";
const basePathExternal = environment.basePathExternal;
const basePathExternalApi = environment.basePathExternalApi;
/**
 * Service class to communicate with the REST services
 */
@Injectable({
  providedIn: 'root',
})
export class FundService {
  constructor(
    private profileManagerService: ProfileManagerService,
    private httpClient: HttpClient,
    private encoder: HttpUrlEncodingCodec,
    private secureStorage:LocalService,
  ) { }
  /**
   * Export the selected elements with the givenFormat
   * @param {string} format
   * @param {string} keys
   * @param {string} criteria
   * @param {string} searchOrder
   * @param {Fund} body
   * @returns {  Observable<File>}
   */
  export(
    format?: string,
    keys?: string,
    criteria?: string,
    searchOrder?: string,
    body?: Fund[]
  ): Observable<any> {
    let params = new HttpParams()
    if (format !== undefined && format !== null) {
      params = params.append(
        'format',
        this.encoder.encodeValue(format.toString())
      )
    }
    if (keys !== undefined && keys !== null) {
      params = params.append('keys', this.encoder.encodeValue(keys.toString()))
    }
    if (criteria !== undefined && criteria !== null) {
      params = params.append(
        'criteria',
        this.encoder.encodeValue(criteria.toString())
      )
    }
    if (searchOrder !== undefined && searchOrder !== null) {
      params = params.append(
        'searchOrder',
        this.encoder.encodeValue(searchOrder.toString())
      )
    }
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post(`${BASE_PATH}/funds/export`, body, {
      params: params,
      headers: headers,
      observe: 'response',
      responseType: 'blob',
    })
  }
  /**
   * Validate the integrity of all fields of the given com.vermeg.portal.operation.common.Fund
   * @param {Fund} body
   * @returns {  Observable<any>}
   */
  validate(body?: Fund): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<any>(`${BASE_PATH}/funds/validate`, body, {
      headers: headers,
    })
  }
  /**
   * Performs a search operation without pagination
   * @param {string} criteria
   * @param {string} order the order fields
   * @param {string} keys the chosen keys
   * @returns {  Observable<any[]>}
   */
  findAll(criteria?: string, order?: string, keys?: string): Observable<any[]> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    let body = null
    let managerCode = null;
    let ptcCode = null;
    body = { "managerCode": null, "ptfCod": null }
    if (this.secureStorage.getJsonValue('portfolioCode')) {
      let portfolioCode = JSON.parse(this.secureStorage.getJsonValue('portfolioCode'))
      if (this.profileManagerService.isMasterBO() || this.profileManagerService.isOperatorBO()) {
        body = { "managerCode": null, "ptfCod": null }

      }
      else {
        managerCode = portfolioCode.managerCode ? portfolioCode.managerCode : null;
        ptcCode = portfolioCode.portfolioCode ? portfolioCode.portfolioCode : null;
        body = { "managerCode": managerCode, "ptfCod": ptcCode }
      }
    }
    return this.httpClient.get<any[]>(`${basePathExternalApi}/getEligibalFundsperUser`,{ headers: headers })

  }
  findAllByGrupoInv(criteria?: string, order?: string, keys?: string): Observable<any[]> {
    return this.httpClient.get<any[]>(`${BASE_PATH}/bff/home/funds/eligibleFunds`, {})
  }
  findAllByPtfCode(criteria?: string, order?: string, keys?: string): Observable<Fund[]> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    let body = null
    let managerCode = null;
    let ptcCode = null;
    body = { "managerCode": null, "ptfCod": null }
    if (this.secureStorage.getJsonValue('portfolioCode')) {
      let portfolioCode = JSON.parse(this.secureStorage.getJsonValue('portfolioCode'))
      if (this.profileManagerService.isMasterBO() || this.profileManagerService.isOperatorBO()) {
        body = { "managerCode": null, "ptfCod": null }
      }
      else {
        managerCode = portfolioCode.managerCode ? portfolioCode.managerCode : null;
        ptcCode = portfolioCode.portfolioCode ? portfolioCode.portfolioCode : null;
        body = { "managerCode": managerCode, "ptfCod": ptcCode }

      }
    }
    return this.httpClient.get<any[]>(`${basePathExternalApi}/getEligibalFundsperUser`,{ headers: headers })
  }

  findFundoCotaAll(criteria?: string, order?: string, keys?: string): Observable<any[]> {

    return this.httpClient.get<any[]>(`${FUNDO_COTA_PATH}`).pipe(
      map((data: any[]) => {
        let list: Fund[] = [];
        data.forEach(element => {
          list.push({
            fundCode: element.codigoId,
            name: element.nome,
            cetipCode: element.accountCetipSelic,
            cnpj: element.fundCNPJ,
            isin: element.isin
          } as Fund);
        });
        return list;

      }));
  }
  /**
   * Find by the fund identifiers
   * @param {string} keys the required keys
   * @returns {Observable<Fund>}
   */
  findByCode(keys?: string): Observable<Fund> {
    let params = new HttpParams()
    if (keys !== undefined && keys !== null) {
      params = params.append('keys', this.encoder.encodeValue(keys.toString()))
    }
    return this.httpClient.get<Fund>(`${BASE_PATH}/funds`, { params: params })
  }
  /**
   * Saves the fund
   * @param {Fund} body
   * @returns {Observable<Fund>}
   */
  insert(body?: Fund): Observable<Fund> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<Fund>(`${BASE_PATH}/funds`, body, {
      headers: headers,
    })
  }
  /**
   * Updates the fund
   * @param {Fund} body
   * @returns {Observable<Fund>}
   */
  update(body?: Fund): Observable<Fund> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.put<Fund>(`${BASE_PATH}/funds`, body, {
      headers: headers,
    })
  }
  /**
   * Deletes all funds with an optional criteria
   * @param {string} criteria
   * @returns {  Observable<number>}
   */
  delete(criteria?: string): Observable<number> {
    let params = new HttpParams()
    if (criteria !== undefined && criteria !== null) {
      params = params.append(
        'criteria',
        this.encoder.encodeValue(criteria.toString())
      )
    }

    return this.httpClient.delete<number>(`${BASE_PATH}/funds`, {
      params: params,
    })
  }
  /**
   * Returns the possible values of the given field
   * @param {string} field
   * @param {string} criteria
   * @param {Fund} body
   * @returns {  Observable<any[]>}
   */
  getPossibleValues(
    field: string,
    criteria?: string,
    body?: Fund
  ): Observable<any[]> {
    let params = new HttpParams()
    if (criteria !== undefined && criteria !== null) {
      params = params.append(
        'criteria',
        this.encoder.encodeValue(criteria.toString())
      )
    }
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<any[]>(
      `${BASE_PATH}/funds/${field}/possibleValues`,
      body,
      { params: params, headers: headers }
    )
  }
  /**
   * Returns the possible values of the given field translated using the current user profile
   * @param {string} field
   * @param {string} keys
   * @param {string} seperator
   * @param {string} criteria
   * @param {Fund} body
   * @returns {  Observable<any[]>}
   */
  getTranslatedPossibleValues(
    field: string,
    keys?: string,
    seperator?: string,
    criteria?: string,
    body?: Fund
  ): Observable<any[]> {
    let params = new HttpParams()
    if (keys !== undefined && keys !== null) {
      params = params.append('keys', this.encoder.encodeValue(keys.toString()))
    }
    if (seperator !== undefined && seperator !== null) {
      params = params.append(
        'seperator',
        this.encoder.encodeValue(seperator.toString())
      )
    }
    if (criteria !== undefined && criteria !== null) {
      params = params.append(
        'criteria',
        this.encoder.encodeValue(criteria.toString())
      )
    }
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<any[]>(
      `${BASE_PATH}/funds/${field}/possibleValues/translated`,
      body,
      { params: params, headers: headers }
    )
  }
  /**
   * Find the fund by code
   * @param {string} code
   * @param {string} keys the required keys
   * @returns {Observable<Fund>}
   */
  findByCodeAsString(code: string, keys?: string): Observable<Fund> {
    let params = new HttpParams()
    if (keys !== undefined && keys !== null) {
      params = params.append('keys', this.encoder.encodeValue(keys.toString()))
    }
    return this.httpClient.get<Fund>(`${BASE_PATH}/funds/${code}`, {
      params: params,
    })
  }
  /**
   * Deletes the fund instance with the given code
   * @param {string} code
   * @returns {  Observable<any>}
   */
  deleteOne(code: string): Observable<any> {
    return this.httpClient.delete<any>(`${BASE_PATH}/funds/${code}`)
  }
  /**
   * Deletes all funds with an optional criteria
   * @param {string} criteria
   * @returns {  Observable<number>}
   */
  deleteWithCriteria(criteria?: string): Observable<number> {
    let params = new HttpParams()
    if (criteria !== undefined && criteria !== null) {
      params = params.append(
        'criteria',
        this.encoder.encodeValue(criteria.toString())
      )
    }

    return this.httpClient.delete<number>(`${BASE_PATH}/funds/delete`, {
      params: params,
    })
  }
  /**
   * Validate the integrity of the fieldName in the given fund instance
   * @param {string} field
   * @param {Fund} body
   * @returns {  Observable<any>}
   */
  validateField(field: string, body?: Fund): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<any>(
      `${BASE_PATH}/funds/${field}/validate`,
      body,
      { headers: headers }
    )
  }
  /**
   * Validate the integrity of all fields of the given Fund
   * @param {Fund} body
   * @returns {  Observable<any>}
   */
  validateAll(body?: Fund): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<any>(`${BASE_PATH}/funds/validate/all`, body, {
      headers: headers,
    })
  }
  /**
   * Evaluates the given formula against the given fund instance
   * @param {string} formula
   * @param {Fund} body
   * @returns {  Observable<any>}
   */
  evaluateFormula(formula?: string, body?: Fund): Observable<any> {
    let params = new HttpParams()
    if (formula !== undefined && formula !== null) {
      params = params.append(
        'formula',
        this.encoder.encodeValue(formula.toString())
      )
    }
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' })
    return this.httpClient.post<any>(`${BASE_PATH}/funds/evaluate`, body, {
      params: params,
      headers: headers,
    })
  }
  /**
   * Counts all the funds with an optional criteria
   * @param {string} criteria
   * @returns {  Observable<number>}
   */
  count(criteria?: string): Observable<number> {
    let params = new HttpParams()
    if (criteria !== undefined && criteria !== null) {
      params = params.append(
        'criteria',
        this.encoder.encodeValue(criteria.toString())
      )
    }
    return this.httpClient.get<number>(`${BASE_PATH}/funds/count`, {
      params: params,
    })
  }
  /**
   * Return a new fund instance initialized from the server
   * @returns {Observable<Fund>}
   */
  newInstance(): Observable<Fund> {
    return this.httpClient.get<Fund>(`${BASE_PATH}/funds/new`)
  }
  /**
   * Performs a paginated search on funds
   * @param {string} criteria
   * @param {string} order the order fields
   * @param {string} keys the chosen keys
   * @param {number} page the page number
   * @param {number} size the page size
   * @returns {  Observable<any[]>}
   */
  find(
    criteria?: string,
    order?: string,
    keys?: string,
    page?: number,
    size?: number
  ): Observable<any[]> {
    let params = new HttpParams()
    if (criteria !== undefined && criteria !== null) {
      params = params.append(
        'criteria',
        this.encoder.encodeValue(criteria.toString())
      )
    }
    if (order !== undefined && order !== null) {
      params = params.append(
        'order',
        this.encoder.encodeValue(order.toString())
      )
    }
    if (keys !== undefined && keys !== null) {
      params = params.append('keys', this.encoder.encodeValue(keys.toString()))
    }
    if (page !== undefined && page !== null) {
      params = params.append('page', this.encoder.encodeValue(page.toString()))
    }
    if (size !== undefined && size !== null) {
      params = params.append('size', this.encoder.encodeValue(size.toString()))
    }
    return this.httpClient.get<any[]>(`${BASE_PATH}/funds/search`, {
      params: params,
    })
  }
}
